import { useCallback, useState } from "react";
import { API_HOST_WMS } from "config/api.config";
import { api } from "network/network";
import { APIendpoints } from "utils/constants/APIendpoints";
import { STATUS_BEGIN, STATUS_DONE, STATUS_FAIL } from "utils/constants/others";

export const STATUS_OUT_OF_RANGE = "OUT_OF_RANGE";
export const STATUS_LOCATION_ERROR = "LOCATION_ERROR";

export const getErrorMessage = (error, fallbackMessage) => {
  const internalError = error?.response?.data?.error?.internal_error;
  if (internalError && typeof internalError === "string") {
    const match = internalError.match(/OrignalError\s*:\s*(.+?),\s*ErrorType/);
    if (match && match[1]) return match[1].trim();
  }

  return (
    error?.response?.data?.error?.message ||
    error?.message ||
    fallbackMessage ||
    "Something went wrong. Please try again."
  );
};

const requestCurrentLocation = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      const error = new Error("Location is not supported on this device.");
      error.isLocationError = true;
      reject(error);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position.coords),
      () => {
        const error = new Error("Unable to fetch your location. Please try again.");
        error.isLocationError = true;
        reject(error);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });

// Drives the markarrived outcome (success / out-of-range / location-error) shared by
// both the shared-link flow (encrypted_code) and the QR/manual-code flow (code).
export const useMarkArrived = () => {
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [radiusInMeters, setRadiusInMeters] = useState(null);

  const confirm = useCallback(({ code, encryptedCode, vehicleNumber }) => {
    if (status === STATUS_BEGIN) return;

    setStatus(STATUS_BEGIN);
    setError("");
    setRadiusInMeters(null);

    requestCurrentLocation()
      .then((coords) =>
        api.post(API_HOST_WMS, APIendpoints.markPlanShipmentArrived, {
          ...(encryptedCode ? { encrypted_code: encryptedCode } : {}),
          ...(code ? { code } : {}),
          vehicle_number: vehicleNumber || "",
          latitude: coords.latitude,
          longitude: coords.longitude,
        })
      )
      .then((response) => {
        const withinRadius = Boolean(response?.data?.response?.within_radius);
        const radius = Number(response?.data?.response?.mark_arrived_radius_in_meters) || null;
        setRadiusInMeters(radius);
        setStatus(withinRadius ? STATUS_DONE : STATUS_OUT_OF_RANGE);
      })
      .catch((confirmError) => {
        if (confirmError?.isLocationError) {
          setStatus(STATUS_LOCATION_ERROR);
          return;
        }
        setStatus(STATUS_FAIL);
        setError(getErrorMessage(confirmError, "Unable to mark entry. Please try again."));
      });
  }, [status]);

  const reset = useCallback(() => {
    setStatus("");
    setError("");
    setRadiusInMeters(null);
  }, []);

  return { status, error, radiusInMeters, confirm, reset };
};

export const isMarkArrivedOutcome = (status) =>
  status === STATUS_DONE || status === STATUS_OUT_OF_RANGE || status === STATUS_LOCATION_ERROR;
